import { Link } from 'react-router-dom';
import { Check, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { NotificationItem } from './NotificationItem';
import { NotificationsEmpty } from './NotificationsEmpty';
import { useNotifications, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useNotifications';
import { Notification } from '../../lib/notifications-api';

interface NotificationsPanelProps {
  onClose?: () => void;
}

export function NotificationsPanel({ onClose }: NotificationsPanelProps) {
  const { data, isLoading, error } = useNotifications(10);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const notifications = data?.pages.flatMap((page) => page.data) || [];
  const hasUnread = notifications.some((n) => !n.isRead);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead.mutate(notification.notificationId);
    }

    // Navigate to action URL if present
    const actionUrl = notification.data?.eventLink || notification.data?.invitationLink;
    if (actionUrl && typeof actionUrl === 'string') {
      window.location.href = actionUrl;
    }

    onClose?.();
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <div className="w-80 md:w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Notificări</h3>
        <div className="flex items-center gap-1">
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsRead.isPending}
              className="text-xs h-8"
            >
              <Check className="w-3.5 h-3.5 mr-1" />
              Citește toate
            </Button>
          )}
          <Link to="/app/notifications/preferences" onClick={onClose}>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Settings className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="py-8 px-4 text-center text-red-500 text-sm">
            Eroare la încărcarea notificărilor
          </div>
        ) : notifications.length === 0 ? (
          <NotificationsEmpty />
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.notificationId}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-100">
          <Link
            to="/app/notifications"
            onClick={onClose}
            className="block text-center text-sm text-terracotta hover:text-terracotta/80 font-medium"
          >
            Vezi toate notificările
          </Link>
        </div>
      )}
    </div>
  );
}
